import { useLocation } from "react-router-dom"
import { Skeleton, SkeletonIcon } from "@/app/components/ui/skeleton"
import { StaticIcon } from "@/shared/icons/StaticIcon"
import { DynamicIcon } from "@/features/navigation/DynamicIcon"
import { ObjectStatus } from "@/db/schema"
import { useSettingBoolean } from "@/features/settings/hooks/useSetting"
import { getCachedContainerMeta } from "@/features/views/loadingPlaceholders"
import { CachedMetadataRow } from "@/features/project-header/CachedMetadataRow"
import { CachedEditorHtml } from "@/features/editor/CachedEditorHtml"
import type { RegisteredIconType } from "@/shared/icons/iconRegistry"

type SkeletonVariant = "default" | "container" | "area"

interface MainSkeletonProps {
  /** "default" for static views, "container" for project/list, "area" for area */
  variant?: SkeletonVariant
}

/** Map pathname → icon type for static views */
const PATHNAME_ICON_MAP: Record<string, RegisteredIconType> = {
  "/": "today",
  "/evening": "today",
  "/inbox": "inbox",
  "/upcoming": "upcoming",
  "/anyday": "anyday",
  "/someday": "someday",
  "/logbook": "logbook",
  "/trash": "trash",
  "/search": "search",
}

/**
 * Skeleton placeholder for the main content area while page data loads.
 *
 * Three variants — each matches its real header 1:1:
 *
 *   - **default**: StaticIcon 24px + title (text-2xl).
 *     Used by Today, Inbox, Upcoming, etc.  (ViewShell header)
 *
 *   - **container**: TimelineIcon large (34px) + title (text-[28px])
 *     + description lines.  Used by Project / List.  (ProjectHeader)
 *
 *   - **area**: StaticIcon "area" 36px + title (text-[28px]).
 *     Used by Area pages.  (AreaHeader)
 *
 * All icons render in skeleton-muted color via `<SkeletonIcon>`.
 */
export function MainSkeleton({ variant = "default" }: MainSkeletonProps) {
  const { pathname } = useLocation()

  return (
    <div className="mx-auto max-w-225">
      {variant === "container" ? (
        <ContainerHeader pathname={pathname} />
      ) : variant === "area" ? (
        <AreaHeader />
      ) : (
        <DefaultHeader pathname={pathname} />
      )}

      {/* Task rows — identical to TaskItem compact row */}
      {[55, 35, 70, 25, 50, 45].map((width, i) => (
        <div key={i} className="task-card-scroll-target">
          <div className="flex min-h-11 items-center gap-2 rounded-lg px-3 py-2">
            <Skeleton className="size-5 shrink-0 rounded-[5px]" />
            <Skeleton className="h-4 rounded" style={{ width: `${width}%` }} />
          </div>
        </div>
      ))}
    </div>
  )
}

/**
 * Default header — matches ViewShell header:
 *   StaticIcon size=36  active  mode="monochrome"
 *   gap-2, title text-[28px] font-medium
 */
function DefaultHeader({ pathname }: { pathname: string }) {
  const iconType = PATHNAME_ICON_MAP[pathname] ?? "today"
  const { value: isColorVariant } = useSettingBoolean("#theme-variant-color")
  const iconMode = isColorVariant ? "color" as const : "monochrome" as const

  return (
    <div className="mb-4 flex items-center gap-2 px-3 desk:px-0">
      <SkeletonIcon>
        <StaticIcon type={iconType} active size={36} mode={iconMode} />
      </SkeletonIcon>
      <Skeleton className="h-7 flex-1 max-w-[50%] rounded" />
    </div>
  )
}

/**
 * Container header — matches ProjectHeader:
 *   TimelineIcon size="large" (34px)  color="var(--primary)"
 *   gap-2, title text-[28px] font-medium
 *   mb-4 px-3 desk:px-0
 *
 * Reads cached metadata (icon, title, tags, startAt, docHtml) from
 * localStorage so the skeleton matches the real header as closely as
 * possible — custom icon, real title, metadata row, editor placeholder.
 */
function ContainerHeader({ pathname }: { pathname: string }) {
  const isListPath = pathname.startsWith("/list")
  const timelineType = isListPath ? "list" : "project"
  const viewId = isListPath ? "list" : "project"

  // Extract container ID from pathname: /project/:id or /list/:id
  const containerId = pathname.split("/")[2] ?? ""
  const cached = containerId ? getCachedContainerMeta(viewId, containerId) : undefined

  return (
    <div className="mb-4 px-3 desk:px-0">
      {/* Header row: icon + title + menu */}
      <div className="flex min-h-9 items-center gap-2">
        <div className="shrink-0">
          <DynamicIcon
            icon={cached?.icon}
            type={timelineType}
            percentage={0}
            unfinished={0}
            status={cached?.status ?? ObjectStatus.Unfinished}
            size="large"
            color="var(--primary)"
          />
        </div>
        {cached?.title ? (
          <h1 className="min-w-0 flex-1 text-[28px] font-medium leading-[1.3]">
            {cached.title}
          </h1>
        ) : (
          <Skeleton className="h-7 flex-1 max-w-[50%] rounded" />
        )}
      </div>

      {/* Cached metadata row — When, Deadline, Tags */}
      {cached && <CachedMetadataRow meta={cached} />}

      {/* Editor area / description placeholder */}
      {cached ? (
        <CachedEditorHtml
          docHtml={cached.docHtml}
          className="mt-2 min-h-[2rem]"
        />
      ) : (
        <div className="mt-3 space-y-1.5">
          <Skeleton className="h-3.5 w-[80%] rounded" />
          <Skeleton className="h-3.5 w-[55%] rounded" />
        </div>
      )}
    </div>
  )
}

/**
 * Area header — matches AreaHeader:
 *   StaticIcon type="area" size=36  active  fillOnActive=false
 *   gap-2, title text-[28px] font-medium
 *   mb-6 px-3 desk:px-0
 */
function AreaHeader() {
  return (
    <div className="mb-6 px-3 desk:px-0">
      <div className="flex min-h-9 items-center gap-2">
        <SkeletonIcon className="shrink-0">
          <StaticIcon type="area" active fillOnActive={false} size={36} />
        </SkeletonIcon>
        <Skeleton className="h-7 flex-1 max-w-[50%] rounded" />
        {/* Menu dots placeholder */}
        <Skeleton className="ml-auto size-6 shrink-0 rounded" />
      </div>
    </div>
  )
}
